import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  TextInput,
  Alert,
  ActivityIndicator,
  SafeAreaView,
} from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';
import type { AnalyzedItem } from '../lib/gemini';
import { Ionicons } from '@expo/vector-icons';
import * as Crypto from 'expo-crypto';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import type { RootStackParamList } from '../types/navigation';
import { getUserProducts, updateUserProduct } from '../utils/userProductStorage';
import { detectInventoryDuplicates } from '../utils/inventoryDuplicateDetection';
import { VariantPicker } from '../components/common/VariantPicker';
import { listSiblingVariants, type SiblingVariant } from '../lib/variantOptions';
import type { CacheListItemData } from '../store/slices/userProductsSlice';
import type { ItemCreationStatus } from '../types/itemStatus';
import { clearPendingAppraisals } from '../lib/pendingAppraisals';
import type { BackendCatalogData } from '../types/newDatabaseTypes';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { ConditionBadge } from '../components/common/ConditionBadge';
import { IdentityQuestion } from '../components/common/IdentityQuestion';
import { answerItemIdentity, identityOutcomeLabel, identityShowsEstimate } from '../lib/itemIdentity';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'AddAnalyzedItem'>;
  route: RouteProp<RootStackParamList, 'AddAnalyzedItem'>;
};

type ItemCondition = 'New' | 'Like New' | 'Excellent' | 'Good' | 'Fair' | 'Poor';

const CONDITIONS: ItemCondition[] = ['New', 'Like New', 'Excellent', 'Good', 'Fair', 'Poor'];

interface EditableItem {
  selected: boolean;
  priceRange: {
    min: number;
    max: number;
  };
  name: string;
  description: string;
  category: string;
  condition: ItemCondition;
  modelCondition: ItemCondition | null;
  estimatedValue: string;
  estimatedValueMin: string;
  estimatedValueMax: string;
  brand: string;
  model: string;
  family?: string;
  originalValue: string;
  purchaseDate: string;
  conditionScore: number | null;
  demandScore: number | null;
  confidenceScore: number | null;
  variantFields?: Array<{ name: string; value: string }>;
  _backendData?: BackendCatalogData;
}

/**
 * Whether Review shows (and Save registers) a value for this item: every item without a T1
 * outcome, plus (S3) a T1 outcome that carries a Cache AI estimate.
 */
function showsPrice(item: EditableItem): boolean {
  const identity = item._backendData?.identity;
  return !identityOutcomeLabel(identity) || identityShowsEstimate(identity, item._backendData?.price_source);
}

export default function AddAnalyzedItemScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        scrollView: {
          flex: 1,
        },
        loadingContainer: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundPrimary,
        },
        imageContainer: {
          height: 250,
          backgroundColor: theme.colors.backgroundSurface,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: theme.spacing.xl,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        image: {
          width: '100%',
          height: '100%',
          resizeMode: 'contain',
        },
        formContainer: {
          padding: theme.spacing.lg,
        },
        fieldGroup: {
          marginBottom: theme.spacing.lg,
        },
        readOnlyField: {
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginTop: theme.spacing.sm,
        },
        readOnlyText: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        estimatedValue: {
          ...theme.typography.valueMd,
          color: theme.colors.accent,
          marginTop: theme.spacing.sm,
        },
        priceRangeText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xs,
        },
        descriptionInput: {
          minHeight: 100,
          textAlignVertical: 'top',
        },
        conditionWrapper: {
          marginTop: theme.spacing.sm,
        },
        marketDemandBar: {
          height: 8,
          backgroundColor: theme.colors.backgroundElevated,
          borderRadius: theme.radius.radiusSm,
          overflow: 'hidden',
          marginTop: theme.spacing.sm,
        },
        marketDemandFill: {
          height: '100%',
          backgroundColor: theme.colors.accent,
        },
        itemCard: {
          marginBottom: theme.spacing.xl,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.backgroundSurface,
          position: 'relative',
          overflow: 'hidden',
        },
        itemCardSelected: {
          borderColor: theme.colors.accent,
        },
        itemContent: {
          padding: theme.spacing.lg,
        },
        itemImageContainer: {
          height: 150,
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        itemImage: {
          width: '100%',
          height: '100%',
          resizeMode: 'contain',
        },
        selectionHeader: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginBottom: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        selectionCount: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        selectAllButton: {
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: 'transparent',
        },
        selectAllText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        checkmarkBadge: {
          position: 'absolute',
          top: theme.spacing.md,
          right: theme.spacing.md,
          width: 44,
          height: 44,
          borderRadius: 22,
          backgroundColor: theme.colors.accent,
          justifyContent: 'center',
          alignItems: 'center',
        },
        actionSection: {
          marginTop: theme.spacing.xl,
          marginBottom: theme.spacing.lg,
        },
        fullWidthButton: {
          width: '100%',
          marginBottom: theme.spacing.md,
        },
        savingIndicator: {
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: theme.spacing.xl,
          gap: theme.spacing.md,
        },
        savingText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        errorBannerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
        },
        errorTextFlex: {
          flex: 1,
        },
        actionHint: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
        },
      }),
    [theme],
  );
  const { items = [], imageStoragePath = null, itemImageMap, multiplePhotosPerItem = false, pendingImageBase64, fromPendingAppraisals = false } = route.params;
  const { user } = useAuth();
  const [editableItems, setEditableItems] = useState<EditableItem[]>(
    items.map(item => ({
      selected: true,
      priceRange: {
        min: item.priceRange?.min || 0,
        max: item.priceRange?.max || 0,
      },
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      condition: (item.condition as ItemCondition) || 'Good',
      modelCondition: (item.condition as ItemCondition) || null,
      estimatedValue: (item.estimatedValue ?? (!item._backendData || item._backendData.price_source === 'gemini_estimate' ? item.priceRange?.min : null))?.toString() || '',
      estimatedValueMin: item.priceRange?.min?.toString() || '',
      estimatedValueMax: item.priceRange?.max?.toString() || '',
      brand: item.brand || '',
      model: item.model || '',
      family: item.family,
      originalValue: item.originalValue?.toString() || '',
      purchaseDate: item.purchaseDate || '',
      conditionScore: item.conditionScore || null,
      demandScore: item.demandScore || null,
      confidenceScore: item.confidenceScore || null,
      variantFields: item.variantFields || undefined,
      _backendData: item._backendData,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Variant picker (T5). Keyed by editableItems index. Catalog-bound: only
  // populated for items whose model has ≥2 catalogued siblings (Q2). The pick
  // is applied as a POST-INSERT update because the BEFORE INSERT backfill
  // trigger clobbers any variant_id supplied at insert time.
  const [siblingsByIndex, setSiblingsByIndex] = useState<Record<number, SiblingVariant[]>>({});
  const [pickedVariants, setPickedVariants] = useState<Record<number, SiblingVariant>>({});
  // T1: at most one structured identity answer per scanned item, keyed by editableItems index.
  // It is sent after insert (ownership is checked against the saved item). S3: null records
  // "Not sure" (nothing is sent; the question stays open on the item screen).
  const [identityAnswers, setIdentityAnswers] = useState<Record<number, string | null>>({});
  const [openPickerIndex, setOpenPickerIndex] = useState<number | null>(null);
  const [publicImageUrl, setPublicImageUrl] = useState<string | null>(null);
  const [publicImageUrls, setPublicImageUrls] = useState<{ [imagePath: string]: string }>({});
  const [loadingUrl, setLoadingUrl] = useState(true);

  // Memoize selected count to avoid repeated filtering
  const selectedCount = useMemo(
    () => editableItems.filter(item => item.selected).length,
    [editableItems]
  );

  // Memoize total count
  const totalCount = editableItems.length;

  useEffect(() => {
    const fetchImageUrls = async () => {
      setLoadingUrl(true);

      if (itemImageMap) {
        // Handle multiple images case
        console.log('[DEBUG] Fetching URLs for multiple images:', Object.values(itemImageMap));
        const urlMap: { [imagePath: string]: string } = {};

        try {
          // Flatten all paths (handle both string and string[] values)
          const allPaths: string[] = [];
          Object.values(itemImageMap).forEach(value => {
            if (Array.isArray(value)) {
              allPaths.push(...value);
            } else {
              allPaths.push(value);
            }
          });

          const uniquePaths = [...new Set(allPaths)];

          for (const path of uniquePaths) {
            const { data } = supabase.storage
              .from('item-images')
              .getPublicUrl(path);

            if (data?.publicUrl) {
              urlMap[path] = data.publicUrl;
              console.log('[DEBUG] Got URL for path:', path, '→', data.publicUrl);
            }
          }

          setPublicImageUrls(urlMap);
        } catch (error) {
          console.error('[ERROR] Failed to fetch multiple image URLs:', error);
        }
      } else if (imageStoragePath) {
        // Handle single image case (backward compatibility)
        console.log('[DEBUG] Attempting to fetch public URL for single path:', imageStoragePath);
        try {
          const { data } = supabase.storage
            .from('item-images')
            .getPublicUrl(imageStoragePath);

          if (data?.publicUrl) {
            console.log('[DEBUG] Public URL received:', data.publicUrl);
            setPublicImageUrl(data.publicUrl);
          } else {
            console.warn('[WARN] Could not get public URL, data received:', data);
            setPublicImageUrl(null);
          }
        } catch (fetchError: any) {
          console.error('[ERROR] Exception fetching public URL:', fetchError);
          setPublicImageUrl(null);
        }
      } else {
        // Check if any item has an image_url (e.g., base64 data URL from pending appraisals)
        const itemWithImage = items.find(item => item.image_url && item.image_url.startsWith('data:'));
        if (itemWithImage?.image_url) {
          console.log('[DEBUG] Using item image_url (base64 data URL)');
          setPublicImageUrl(itemWithImage.image_url);
        } else {
          console.log('[DEBUG] No image storage path provided.');
          setPublicImageUrl(null);
        }
      }

      setLoadingUrl(false);
    };

    fetchImageUrls();
  }, [imageStoragePath, itemImageMap, items]);

  const toggleItemSelection = (index: number) => {
    const newItems = [...editableItems];
    newItems[index] = {
      ...newItems[index],
      selected: !newItems[index].selected,
    };
    setEditableItems(newItems);
  };

  // Load catalogued sibling variants for each item's model (T5). Keyed off
  // the backend-assigned model_id; only items with ≥2 siblings get a picker.
  // Depends on `items` (the stable route param) so editing a name/description
  // doesn't re-fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map: Record<number, SiblingVariant[]> = {};
      await Promise.all(
        items.map(async (it, idx) => {
          const modelId = it._backendData?.model_id;
          if (!modelId) return;
          const variants = await listSiblingVariants(modelId, it._backendData?.variant_id ?? null);
          if (variants.length >= 2) map[idx] = variants;
        }),
      );
      if (!cancelled) setSiblingsByIndex(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const updateItem = (index: number, updates: Partial<EditableItem>) => {
    const newItems = [...editableItems];
    newItems[index] = {
      ...newItems[index],
      ...updates,
    };
    setEditableItems(newItems);
  };



  const handleSave = async (status: ItemCreationStatus) => {
    try {
      setSaving(true);
      setSaveError(null);
      const selectedItems = editableItems.filter(item => item.selected);
      console.log('[DEBUG] Saving user products:', selectedItems, 'with status:', status);

      // Create brands, families, models, variants, and user products
      const userProducts: any[] = [];
      // Variant picks to apply AFTER insert (T5): the BEFORE INSERT backfill
      // trigger overwrites variant_id at insert, so a deliberate pick must be
      // re-applied as an update. Each entry's `id` is the row's user_products.id.
      const postInsertPicks: { id: string; picked: SiblingVariant }[] = [];
      // T1: identity answers to send after insert ({ id, axis, value }).
      const postInsertAnswers: { id: string; axis: string; value: string; revision: string }[] = [];

      for (let i = 0; i < selectedItems.length; i++) {
        const item = selectedItems[i];
        // Find the original index of this item in the editableItems array
        const originalIndex = editableItems.findIndex(editableItem =>
          editableItem === item || (
            editableItem.name === item.name &&
            editableItem.description === item.description &&
            editableItem.category === item.category
          )
        );
        // Use backend's variant_id if available (cache hit), otherwise NULL.
        // The backend background task (run_catalog_matching) will populate it
        // and propagate to user_products once catalog matching completes.
        const variantId = item._backendData?.variant_id ?? null;
        if (variantId) {
          console.log('[DEBUG] Using backend catalog variant_id:', variantId);
        } else {
          console.log('[DEBUG] Backend has not yet assigned variant_id for:', item.name, '— saving with NULL, backend will backfill');
        }

        // Get the correct image path(s) for this specific item
        let imagePaths: string[] = [];

        if (multiplePhotosPerItem && itemImageMap && itemImageMap[originalIndex]) {
          // Multiple photos of the same item - itemImageMap[index] is an array
          const paths = itemImageMap[originalIndex];
          imagePaths = Array.isArray(paths) ? paths : [paths];
        } else if (itemImageMap && itemImageMap[originalIndex]) {
          // Single photo per item (bulk upload mode)
          const path = itemImageMap[originalIndex];
          imagePaths = Array.isArray(path) ? path : [path];
        } else if (imageStoragePath) {
          // Fallback to single image path
          imagePaths = [imageStoragePath];
        } else if (pendingImageBase64 && user?.id) {
          // Upload pending appraisal base64 image to storage
          console.log('[DEBUG] Uploading pending appraisal image to storage...');
          try {
            const filePath = `${user.id}/${Crypto.randomUUID()}.jpg`;
            const binaryString = atob(pendingImageBase64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const arrayBuffer = bytes.buffer;

            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('item-images')
              .upload(filePath, arrayBuffer, {
                contentType: 'image/jpeg',
                upsert: false,
              });

            if (uploadError) {
              console.error('[ERROR] Failed to upload pending image:', uploadError);
            } else if (uploadData?.path) {
              console.log('[DEBUG] Uploaded pending image to:', uploadData.path);
              imagePaths = [uploadData.path];
            }
          } catch (uploadErr) {
            console.error('[ERROR] Exception uploading pending image:', uploadErr);
          }
        }

        // Prepare the user_product entry
        // Use backend's product_id as user_products.id so it matches
        // user_identified_items.product_inventory_id — enabling the trigger
        // and backend propagation to link the two records.
        // `toFiniteOrNull` preserves a legitimate 0 (which `|| null` would drop).
        const toFiniteOrNull = (s: string | null | undefined): number | null => {
          if (!s) return null;
          const n = parseFloat(s);
          return Number.isFinite(n) ? n : null;
        };
        // A user variant pick (T5) needs a known row id so it can be applied
        // post-insert. Use the backend product_id when present, else generate a
        // UUID and use it as the insert id (valid; the trigger/backfill key off
        // id regardless). Only matters when a pick was actually made.
        const picked = pickedVariants[originalIndex];
        const ensuredId = item._backendData?.product_id ?? (picked ? Crypto.randomUUID() : undefined);
        if (picked && ensuredId && picked.variantId !== (variantId || null)) {
          postInsertPicks.push({ id: ensuredId, picked });
        }
        const question = item._backendData?.identity?.question;
        const revision = item._backendData?.identity?.question_revision;
        const answered = identityAnswers[originalIndex];
        if (question && revision && answered && item._backendData?.product_id) {
          postInsertAnswers.push({ id: item._backendData.product_id, axis: question.axis, value: answered, revision });
        }

        const userProduct = {
          ...(ensuredId ? { id: ensuredId } : {}),
          user_id: user?.id || '',
          variant_id: variantId || null, // May be NULL — backend backfills via run_catalog_matching
          condition: item.condition || 'Good',
          // V3 P7: condition provenance. A Gemini condition shown and saved without edit
          // is model_inferred; the 'Good' fallback is a client default. No valuation or
          // currency is written: the item's value belongs to the P8 projector (C9).
          condition_source: item.modelCondition ? 'model_inferred' : 'client_default',
          purchase_price: toFiniteOrNull(item.originalValue),
          purchase_period: item.purchaseDate || null,
          images: imagePaths.map(path => ({ storage_path: path })),
          image_analysis: {
            // Suppressed T1 decisions retain V3/T1 ownership. S1b can recover
            // an instrument estimate even when registered metadata is null.
            // S3: a T1 outcome carrying a Cache AI estimate registers it as the fallback.
            ...(showsPrice(item) ? {
              restoration_valuation_version: '1',
              restoration_initial_valuation: toFiniteOrNull(item.estimatedValue),
              restoration_gemini_estimate: item._backendData?.gemini_estimate
                ?? (!item._backendData || item._backendData.valuation_source === 'gemini_estimate' || item._backendData.price_source === 'gemini_estimate'
                  ? toFiniteOrNull(item.estimatedValue) : null),
            } : {}),
            brand: item.brand,
            model: item.model,
            family: item.family,
            condition_score: item.conditionScore,
            demand_score: item.demandScore,
            confidence_score: item.confidenceScore,
            description: item.description,
            category: item.category,
            variant_fields: item.variantFields,
            ...(showsPrice(item) ? {
              valuation_min: toFiniteOrNull(item.estimatedValueMin),
              valuation_max: toFiniteOrNull(item.estimatedValueMax),
            } : {}),
          },
          status: status === 'owned' ? 'owned' : 'tracking',
        };

        userProducts.push(userProduct);
      }

      console.log('[DEBUG] Saving user products:', userProducts);

      if (userProducts.length === 0) {
        console.warn('[WARN] No valid items to save');
        Alert.alert('Warning', 'No valid items to save. Please try again with different product details.');
        setSaving(false);
        return;
      }

      // Type assertion to ensure TypeScript knows these are valid items
      const { error } = await supabase
        .from('user_products')
        .insert(userProducts as any); // Using 'any' here to bypass TypeScript's strict checking

      if (error) {
        console.error('[ERROR] Error inserting user_products:', error);
        throw error;
      }

      console.log('[DEBUG] Successfully saved items to user_products');

      // Apply variant picks as post-insert updates (T5). variant_source='user'
      // arms the T3b propagation lock so background catalog matching won't
      // revert the choice. V3 P7: no valuation/currency co-write — the catalogue
      // price of the chosen sibling is not the item's value.
      //
      // updateUserProduct returns null on failure. The items themselves are
      // already inserted, so a failed pick must not fail (and risk re-running)
      // the whole save — but it must not be swallowed either: the deliberate
      // pick and its variant_source='user' lock were NOT applied, so collect
      // the failures and warn the user rather than reporting a clean success.
      const failedPicks: string[] = [];
      for (const { id, picked } of postInsertPicks) {
        const applied = await updateUserProduct(id, {
          variant_id: picked.variantId,
          variant_source: 'user',
        });
        if (!applied) {
          console.error('[ERROR] Failed to apply variant selection for product', id);
          failedPicks.push(id);
        }
      }
      // T1: send identity answers for the saved items. A failure leaves the item saved with its
      // question open on the item screen, so it is reported, not retried here.
      let failedAnswers = 0;
      if (postInsertAnswers.length > 0) {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        for (const { id, axis, value, revision } of postInsertAnswers) {
          try {
            if (!accessToken) throw new Error('No session');
            await answerItemIdentity(
              id,
              { axis, value, requestKey: Crypto.randomUUID(), questionRevision: revision },
              accessToken,
            );
          } catch (answerError) {
            console.error('[ERROR] Failed to apply identity answer for product', id, answerError);
            failedAnswers += 1;
          }
        }
      }
      if (failedAnswers > 0) {
        Alert.alert(
          'Details not saved',
          'Your item was added, but we couldn\'t save the detail you chose. You can add it from the item\'s screen.',
          [{ text: 'OK' }],
        );
      }
      if (failedPicks.length > 0) {
        const plural = failedPicks.length > 1;
        Alert.alert(
          'Variant selection not saved',
          `Your item${plural ? 's were' : ' was'} added, but we couldn't apply your ` +
            `variant selection for ${failedPicks.length} item${plural ? 's' : ''}. ` +
            `You can set it from the item's detail screen.`,
          [{ text: 'OK' }],
        );
      }

      // Clear pending appraisals after successful save (if this was from pending appraisals flow)
      if (fromPendingAppraisals) {
        await clearPendingAppraisals();
        console.log('[DEBUG] Cleared pending appraisals after successful save');
      }

      // Check for duplicates after saving
      if (user?.id) {
        try {
          const allUserProducts = await getUserProducts(user.id);
          const duplicateGroups = detectInventoryDuplicates(allUserProducts as CacheListItemData[]);

          if (duplicateGroups.length > 0) {
            // Found duplicates - show alert and navigate to merge screen
            Alert.alert(
              'Potential Duplicates Found',
              `We found ${duplicateGroups.length} group${duplicateGroups.length !== 1 ? 's' : ''} of potential duplicates. Would you like to review them now?`,
              [
                {
                  text: 'Later',
                  style: 'cancel',
                  onPress: () => {
                    navigation.replace('SaveSuccess', {
                      itemCount: userProducts.length,
                    });
                  },
                },
                {
                  text: 'Review Now',
                  onPress: () => {
                    navigation.replace('MergeDuplicates', {
                      items: allUserProducts as CacheListItemData[],
                    });
                  },
                },
              ]
            );
          } else {
            // No duplicates - proceed to success screen
            navigation.replace('SaveSuccess', {
              itemCount: userProducts.length,
            });
          }
        } catch (dupError) {
          console.error('[ERROR] Error checking for duplicates:', dupError);
          // If duplicate check fails, still show success
          navigation.replace('SaveSuccess', {
            itemCount: userProducts.length,
          });
        }
      } else {
        // No user - just show success
        navigation.replace('SaveSuccess', {
          itemCount: userProducts.length,
        });
      }
    } catch (error) {
      console.error('[ERROR] Failed to save user products:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save items. Please try again.';
      setSaveError(errorMessage);
      setSaving(false);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = selectedCount === totalCount;
    setEditableItems(editableItems.map(item => ({
      ...item,
      selected: !allSelected,
    })));
  };

  return (
    <SafeAreaView style={styles.container}>
      {loadingUrl ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color={theme.colors.accent} />
        </View>
      ) : (
        <ScrollView style={styles.scrollView}>
          {publicImageUrl && (
            <View style={styles.imageContainer}>
              <Image source={{ uri: publicImageUrl }} style={styles.image} />
            </View>
          )}

          <View style={styles.formContainer}>
            {/* Multi-item selection header */}
            {totalCount > 1 && (
              <View style={styles.selectionHeader}>
                <Text style={styles.selectionCount}>
                  {selectedCount} of {totalCount} items selected
                </Text>
                <TouchableOpacity
                  style={styles.selectAllButton}
                  onPress={toggleSelectAll}
                >
                  <Text style={styles.selectAllText}>
                    {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {editableItems.map((item, index) => {
              // Get the image URL for this specific item
              const itemImagePath = itemImageMap ? itemImageMap[index] : imageStoragePath;
              // Handle both string and string[] paths
              const firstPath = Array.isArray(itemImagePath) ? itemImagePath[0] : itemImagePath;
              const itemImageUrl = firstPath ? publicImageUrls[firstPath] || publicImageUrl : publicImageUrl;

              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.itemCard,
                    item.selected && styles.itemCardSelected,
                  ]}
                  onPress={() => toggleItemSelection(index)}
                  activeOpacity={0.95}
                >
                  {/* Show individual image for each item in bulk mode */}
                  {itemImageUrl && itemImageMap && (
                    <View style={styles.itemImageContainer}>
                      <Image source={{ uri: itemImageUrl }} style={styles.itemImage} />
                    </View>
                  )}

                  <View style={styles.itemContent}>
                    {!showsPrice(item) ? (
                      // T1: an identity Cache cannot price shows its outcome, never a price.
                      <View style={styles.fieldGroup} testID="identity-outcome">
                        <Text style={commonStyles.sectionLabel}>Valuation</Text>
                        {item._backendData?.identity?.question ? (
                          <IdentityQuestion
                            question={item._backendData.identity.question}
                            selected={identityAnswers[index]}
                            onSelect={(value) =>
                              setIdentityAnswers((prev) => ({ ...prev, [index]: value }))
                            }
                            onUnsure={() =>
                              setIdentityAnswers((prev) => ({ ...prev, [index]: null }))
                            }
                          />
                        ) : (
                          <Text style={styles.priceRangeText}>
                            {identityOutcomeLabel(item._backendData?.identity)}
                          </Text>
                        )}
                      </View>
                    ) : (
                      <View style={styles.fieldGroup}>
                        <Text style={commonStyles.sectionLabel}>
                          {!item.estimatedValue ? 'Valuation' : item._backendData?.valuation_source === 'market_data' ? 'Qualified market asking price' : 'Cache AI estimate'}
                        </Text>
                        <Text style={styles.estimatedValue}>
                          {item.estimatedValue ? `$${item.estimatedValue}` : 'Valuation unavailable'}
                        </Text>
                        {item.estimatedValue ? <Text style={styles.priceRangeText}>
                          Range: ${item.priceRange.min.toFixed(2)} – ${item.priceRange.max.toFixed(2)}
                        </Text> : null}
                        {/* S3: an item still missing a material detail asks it beside its estimate. */}
                        {item._backendData?.identity?.question ? (
                          <IdentityQuestion
                            question={item._backendData.identity.question}
                            selected={identityAnswers[index]}
                            onSelect={(value) =>
                              setIdentityAnswers((prev) => ({ ...prev, [index]: value }))
                            }
                            onUnsure={() =>
                              setIdentityAnswers((prev) => ({ ...prev, [index]: null }))
                            }
                          />
                        ) : null}
                      </View>
                    )}

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Market Demand</Text>
                      <View style={styles.marketDemandBar}>
                        <View
                          style={[
                            styles.marketDemandFill,
                            { width: `${(item.demandScore || 0) * 100}%` },
                          ]}
                        />
                      </View>
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Brand</Text>
                      <View style={styles.readOnlyField}>
                        <Text style={styles.readOnlyText}>{item.brand || 'N/A'}</Text>
                      </View>
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Model</Text>
                      <View style={styles.readOnlyField}>
                        <Text style={styles.readOnlyText}>{item.model || 'N/A'}</Text>
                      </View>
                    </View>

                    {/* Choose the right variant (T5). Shown only when this
                        item's model has ≥2 catalogued siblings (Q2). The pick
                        is applied as a post-insert update in handleSave. */}
                    {siblingsByIndex[index] && (
                      <View style={styles.fieldGroup}>
                        {openPickerIndex !== index ? (
                          <TouchableOpacity
                            testID={`open-variant-picker-${index}`}
                            accessibilityRole="button"
                            onPress={() => setOpenPickerIndex(index)}
                          >
                            <Text style={commonStyles.inputLabel}>
                              {pickedVariants[index]
                                ? 'Variant selected ✓ (tap to change)'
                                : 'Choose the right variant'}
                            </Text>
                          </TouchableOpacity>
                        ) : (
                          <VariantPicker
                            showPrices={false}
                            variants={siblingsByIndex[index]}
                            onSelect={(variant) => {
                              setPickedVariants((prev) => ({ ...prev, [index]: variant }));
                              setOpenPickerIndex(null);
                            }}
                          />
                        )}
                      </View>
                    )}

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Name</Text>
                      <TextInput
                        style={commonStyles.input}
                        value={item.name}
                        onChangeText={(text) => updateItem(index, { name: text })}
                        placeholder="Item Name"
                        placeholderTextColor={theme.colors.textTertiary}
                        onFocus={(e) => e.stopPropagation()}
                      />
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Description</Text>
                      <TextInput
                        style={[commonStyles.input, styles.descriptionInput]}
                        value={item.description}
                        onChangeText={(text) => updateItem(index, { description: text })}
                        placeholder="Item Description"
                        placeholderTextColor={theme.colors.textTertiary}
                        multiline
                        onFocus={(e) => e.stopPropagation()}
                      />
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Category</Text>
                      <View style={styles.readOnlyField}>
                        <Text style={styles.readOnlyText}>{item.category || 'N/A'}</Text>
                      </View>
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={commonStyles.inputLabel}>Condition</Text>
                      <View style={styles.conditionWrapper}>
                        <ConditionBadge condition={item.condition} />
                      </View>
                    </View>
                  </View>

                  {/* Selection badge */}
                  {item.selected && (
                    <View style={styles.checkmarkBadge} pointerEvents="none">
                      <Ionicons name="checkmark" size={28} color={theme.colors.backgroundPrimary} />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}

            {/* Action Section at bottom of scroll */}
            <View style={styles.actionSection}>
              <TouchableOpacity
                style={[
                  commonStyles.primaryButton,
                  styles.fullWidthButton,
                  (saving || selectedCount === 0) && commonStyles.disabledButton,
                ]}
                onPress={() => handleSave('owned')}
                disabled={saving || selectedCount === 0}
              >
                <Ionicons name="cube" size={20} color={theme.colors.backgroundPrimary} />
                <Text style={commonStyles.primaryButtonText}>Add to Cache</Text>
              </TouchableOpacity>

              {/* Loading indicator */}
              {saving && (
                <View style={styles.savingIndicator}>
                  <ActivityIndicator size="large" color={theme.colors.accent} />
                  <Text style={styles.savingText}>
                    {selectedCount === 1
                      ? 'Saving your item...'
                      : `Saving ${selectedCount} items...`}
                  </Text>
                </View>
              )}

              {/* Error message */}
              {saveError && !saving && (
                <View style={[commonStyles.errorContainer, styles.errorBannerRow]}>
                  <Ionicons name="alert-circle" size={20} color={theme.colors.destructive} />
                  <Text style={[commonStyles.errorText, styles.errorTextFlex]}>{saveError}</Text>
                </View>
              )}

              {!saving && !saveError && (
                <Text style={styles.actionHint}>
                  {selectedCount === 0
                    ? 'Select at least one item to continue'
                    : `Ready to add ${selectedCount === 1 ? 'this item' : `${selectedCount} items`} to your cache`}
                </Text>
              )}
            </View>
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}
